import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useState, useEffect } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { InputText } from 'primereact/inputtext';
import { handlePostRequest } from '../../services/PostTemplate';
import classNames from 'classnames';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { handleGetRequest } from '../../services/GetTemplate';
import { Dropdown } from 'primereact/dropdown';

function Reports() {
    const [reportsData, setReportData] = useState();
    const [zonesList, setZonesList] = useState([]);

    const dispatch = useDispatch();

    const formatDate = (d) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    // compute current week's Monday (start) and Sunday (end)
    const getCurrentWeekRange = () => {
        const now = new Date();
        // getDay: 0 (Sun) - 6 (Sat). We want Monday as start.
        const day = now.getDay();
        // distance to Monday
        const diffToMonday = (day + 6) % 7; // if day=1(Mon)->0, day=0(Sun)->6
        const monday = new Date(now);
        monday.setDate(now.getDate() - diffToMonday);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        return { weekStart: formatDate(monday), weekEnd: formatDate(sunday) };
    };

    const getZones = async () => {
        const response = await handleGetRequest('Zone/getAllZones', false);
        if (response?.success === true) {
            setZonesList(response?.data);
        }
    };

    useEffect(() => {
        getZones();
    }, []);

    const { weekStart, weekEnd } = getCurrentWeekRange();

    const formik = useFormik({
        initialValues: {
            fromDate: weekStart || null,
            toDate: weekEnd || null,
            zoneId: '' || null
        },
        onSubmit: async (data) => {
            await fetchReports(data);
        }
    });

    const [loadingReports, setLoadingReports] = useState(false);

    const fetchReports = async (values = formik.values) => {
        setLoadingReports(true);
        try {
            // disable global loader/toast/modal for report fetches (we use local overlay spinner instead)
            const response = await dispatch(handlePostRequest(values, 'revenueReport/GetZoneRevenueReport', false, false, '', '', false));
            if (response?.success === true) {
                setReportData(response?.data);
            } else {
                setReportData([]);
            }
        } catch (err) {
            setReportData([]);
        } finally {
            setLoadingReports(false);
        }
    };

    // Auto-fetch when fromDate, toDate, or zoneId change (debounced)
    useEffect(() => {
        const timer = setTimeout(() => {
            if (formik.values.fromDate && formik.values.toDate) {
                fetchReports();
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [formik.values.fromDate, formik.values.toDate, formik.values.zoneId]);
    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
    };

    const handlePrint = () => {
        const doc = new jsPDF('p', 'pt', 'a4');

        doc.text('Revenue Report', 40, 40);

        const tableColumn = ['Zone Name', 'Vehicles Parked', 'Total Revenue'];
        const tableRows = [];

        reportsData.forEach((item) => {
            tableRows.push([item.zoneName, item.vehiclesParked, item.totalRevenue]);
        });

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 60
        });

        doc.save('revenue-report.pdf');
    };

    const date = new Date().toISOString().split('T')[0];

    return (
        <>
            <div className="grid">
                <div className="col-12">
                    <div className="Card__Round pt-5" style={{ border: 'none' }}>
                        <div className="flex justify-between items-center w-full">
                            <div>
                                <h3 className="mb-0">Revenue Report</h3>
                                <p className="mt-0" style={{ fontSize: '20px' }}>
                                    View detailed revenue insights and performance metrics for every zone
                                </p>
                            </div>

                            <div className="flex gap-2 justify-end ml-auto" style={{ width: '30%' }}>
                                <Button label="Export" icon="pi pi-upload" className="p-button-secondary Black__Btn ml-auto" onClick={handlePrint} />
                            </div>
                        </div>
                        <form onSubmit={formik.handleSubmit} className="p-fluid p-mt-2">
                            <div className="p-fluid p-formgrid grid mb-5 col-12 md:col-12">
                                <div className="p-fluid p-formgrid grid col-12 md:col-4">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="fromDate" className={classNames({ 'p-erro': isFormFieldValid('fromDate') }, 'Label__Text')}>
                                                Start Date <span className="Label__Required">*</span>
                                            </label>
                                            <InputText
                                                max={date}
                                                type="date"
                                                id="fromDate"
                                                name="fromDate"
                                                value={formik?.values?.fromDate || ''}
                                                onChange={(e) => formik.setFieldValue('fromDate', e.target.value === '' ? null : e.target.value)}
                                                className={classNames({ 'p-invalid': isFormFieldValid('fromDate') }, 'Input__Round mt-1')}
                                            />
                                            {getFormErrorMessage('fromDate')}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-fluid p-formgrid grid col-12 md:col-4">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="toDate" className={classNames({ 'p-erro': isFormFieldValid('toDate') }, 'Label__Text')}>
                                                End Date <span className="Label__Required">*</span>
                                            </label>
                                            <InputText
                                                min={formik.values.fromDate}
                                                max={date}
                                                type="date"
                                                id="toDate"
                                                name="toDate"
                                                value={formik?.values?.toDate || ''}
                                                onChange={(e) => formik.setFieldValue('toDate', e.target.value === '' ? null : e.target.value)}
                                                className={classNames({ 'p-invalid': isFormFieldValid('toDate') }, 'Input__Round mt-1')}
                                            />
                                            {getFormErrorMessage('toDate')}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-fluid p-formgrid grid col-12 md:col-4">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="zone" className={classNames({ 'p-erro': isFormFieldValid('zone') }, 'Label__Text')}>
                                                Zone <span className="Label__Required">*</span>
                                            </label>
                                            <Dropdown
                                                placeholder="--Select--"
                                                options={zonesList}
                                                optionValue="zoneID"
                                                optionLabel="zoneName"
                                                id="zoneId"
                                                name="zoneId"
                                                value={formik?.values?.zoneId || ''}
                                                onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('zoneId') }, 'Dropdown__Round mt-1')}
                                            />
                                            {getFormErrorMessage('zoneId')}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-fluid p-formgrid grid col-12 md:col-4">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <Button label="Search" className="Btn__Dark mt-4" style={{ height: '40px', width: '180px' }} type="submit" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div className="Table__Section mt-6" style={{ position: 'relative' }}>
                            {loadingReports && (
                                <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                                </div>
                            )}
                            <DataTable
                                paginator
                                rows={5}
                                rowsPerPageOptions={[5, 10, 25]}
                                className="datatable-responsive"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Records"
                                globalFilterFields={['accountLevelName', 'createuser', 'createdate', 'lkpStatus.statusName']}
                                emptyMessage="No List found."
                                responsiveLayout="scroll"
                                value={reportsData}
                            >
                                <Column field="zoneName" header="Zone Name" />
                                <Column field="vehiclesParked" header="Vehicles Parked" />
                                {/* <Column field="sector" header="Sector" /> */}
                                <Column field="totalRevenue" header="Total Revenue" />
                            </DataTable>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Reports;
